import { createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import { DateTime } from 'luxon';
import { apiGateway } from '../../../../config';

import type { AxiosResponse } from 'axios';
import type { PayloadAction } from '@reduxjs/toolkit';
import type { AppThunk } from '../../../../redux/store';
import type { ErrorResponse, MailTarget } from 'src/@types/common';
import type { MailState, MailDestination, createMailingParams, updateMailingParams } from '../../../../@types/mail';

// ----------------------------------------------------------------------

const initialState: MailState = {
  isLoading: false,
  hasError: false,
  isModalOpen: false,
  message: undefined,
  mailList: undefined,
  mail: undefined,
  mailTargetGroup: [],
};

const slice = createSlice({
  name: 'masterMail',
  initialState,
  reducers: {
    startLoading(state: MailState) {
      state.isLoading = true;
    },

    hasError(state: MailState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = true;
      state.message = action.payload;
    },

    openModal(state: MailState, action: PayloadAction<boolean>) {
      state.isModalOpen = action.payload;
    },

    showMessage(state: MailState, action: PayloadAction<string>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = action.payload;
    },

    resetMessage(state: MailState) {
      state.isLoading = false;
      state.hasError = false;
      state.message = '';
    },

    getMailListSuccess(state: MailState, action: PayloadAction<MailDestination[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.message = undefined;
      state.mailList = action.payload;
      // console.log(state.mailList);
    },

    getMailSuccess(state: MailState, action: PayloadAction<MailDestination>) {
      state.isLoading = false;
      state.hasError = false;
      state.mail = action.payload;
    },

    setMailTarget(state: MailState, action: PayloadAction<MailTarget[]>) {
      state.isLoading = false;
      state.hasError = false;
      state.mailTargetGroup = action.payload;
      // console.log(state.mailTargetGroup)
    },

    createNewMail(state: MailState) {
      const newMail: MailDestination = {
        id: 0,
        name: 'name',
        mailTargetGroupId: 1,
        email: '',
        comment: null,
        createdBy: DateTime.fromJSDate(new Date()).toUTC().toString(),
        createdAt: DateTime.fromJSDate(new Date()).toUTC().toString(),
        updatedBy: null,
        updatedAt: null,
        isEnabled: true,
        isDeleted: false,
      };
      state.mail = newMail;
    },
  },
});

export default slice.reducer;

// ----------------------------------------------------------------------

export function resetMessage(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.resetMessage());
  };
}

export function showError(message: string): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.hasError(message));
  };
}

export function openModal(open: boolean): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.openModal(open));
  };
}

export function createNewMail(): AppThunk {
  return (dispatch): void => {
    dispatch(slice.actions.createNewMail());
  };
}

export function getOptionsMailTarget(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<MailTarget[]>(apiGateway + '/options/mailTarget', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.setMailTarget(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function getMail(id: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<MailDestination>(apiGateway + '/mailing/' + id, {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      // console.log(response.data);
      dispatch(slice.actions.getMailSuccess(response.data));
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function getMailList(): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());
    try {
      const token = (await fetchAuthSession()).tokens?.idToken?.toString();
      const response: AxiosResponse = await axios.get<MailDestination[]>(apiGateway + '/mailing?filter=""', {
        headers: {
          Authorization: 'Bearer ' + token,
        },
        timeout: 60000,
      });
      dispatch(slice.actions.getMailListSuccess(response.data));
    } catch (error) {
      // TODO:以下のエラー処理を有効化
      if (axios.isAxiosError(error)) {
        // error: AxiosError
        if (error.response) {
          const serverResponse: ErrorResponse = error.response.data;
          dispatch(slice.actions.hasError(serverResponse.message));
        } else {
          dispatch(slice.actions.hasError(error.message));
        }
      } else {
        if (error instanceof Error) {
          // error: Error
          dispatch(slice.actions.hasError(error.message));
        } else {
          const message = 'Unknown error';
          dispatch(slice.actions.hasError(message));
        }
      }
    }
  };
}

export function createMail(params: createMailingParams): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response: AxiosResponse = await axios.post<MailDestination>(apiGateway + '/mailing', params, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
      timeout: 60000,
    });

    // console.log('🏰response', response);
    dispatch(slice.actions.showMessage('successfully created'));
  };
}

export function updateMail(id: string, params: updateMailingParams): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response: AxiosResponse = await axios.put<MailDestination>(apiGateway + '/mailing/' + id, params, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
      timeout: 60000,
    });

    // console.log('🏰response', response);
    dispatch(slice.actions.showMessage('successfully updated'));
  };
}

export function deleteMail(id: string): AppThunk {
  return async (dispatch): Promise<void> => {
    dispatch(slice.actions.startLoading());

    const token = (await fetchAuthSession()).tokens?.idToken?.toString();
    const response: AxiosResponse = await axios.delete<MailDestination>(apiGateway + '/mailing/' + id, {
      headers: {
        Authorization: 'Bearer ' + token,
      },
      timeout: 60000,
    });

    // console.log('🏰response', response);
    dispatch(slice.actions.showMessage('successfully deleted'));
  };
}
